// Canvas pointer interaction: select, drag-move (snap + smart guides), marquee, resize handles,
// rotate-90 button, hover, cursor readout, wheel zoom, context menus.
import { updateField } from '../itf/label.js';
import * as barcode from '../itf/barcode.js';
import { S, emit, mutate, commit, select, isLocked, editableSelection, field } from './state.js';
import { $, DOT } from './dom.js';
import { localBox, fieldBounds, rotateVec, unionBounds, intersects } from './geometry.js';
import { ppu, setZoom } from './view.js';
import * as A from './actions.js';
import { openFieldContextMenu, openCanvasContextMenu } from './menus.js';

const DRAG_START_PX = 3;
const SNAP_PX = 6;
let gesture = null;

export function svgPoint(e) {
  const r = $('#label-svg').getBoundingClientRect();
  const k = S.label ? r.width / S.label.width : ppu();
  return { x: (e.clientX - r.left) / k, y: (e.clientY - r.top) / k };
}

/** Topmost field under a label-space point (rotation aware). */
export function hitTest(pt, slackPx = 3) {
  const slack = slackPx / ppu();
  for (let i = S.fields.length - 1; i >= 0; i--) {
    const f = S.fields[i];
    const b = localBox(f);
    const p = rotateVec(pt.x - (f.x || 0), pt.y - (f.y || 0), -(f.rotation || 0));
    if (p.x >= b.x - slack && p.x <= b.x + b.w + slack && p.y >= b.y - slack && p.y <= b.y + b.h + slack) return f;
  }
  return null;
}

const isOneD = (f) => {
  const spec = Array.isArray(barcode.SYMBOLOGIES) ? barcode.SYMBOLOGIES.find((s) => s.id === f.symbology) : null;
  return spec ? spec.oneD !== false : !['DataMatrix', 'QR'].includes(f.symbology);
};

// ----- drag move -------------------------------------------------------------------------------
function startDrag(e, pt, hit) {
  const additive = e.shiftKey || e.metaKey || e.ctrlKey;
  if (additive) {
    select([hit.key], { toggle: true });
    return null;
  }
  if (!S.selection.includes(hit.key)) select([hit.key]);
  if (isLocked(hit)) return null;
  if (e.altKey) A.duplicateSelected();
  const moving = editableSelection();
  if (!moving.length) return null;
  const targets = S.fields.filter((f) => !moving.some((m) => m.key === f.key)).map((f) => fieldBounds(f));
  return {
    type: 'drag',
    start: pt,
    clickKey: hit.key,
    moved: false,
    axis: null,
    initial: moving.map((f) => ({ key: f.key, x: f.x || 0, y: f.y || 0 })),
    bounds0: unionBounds(moving.map((f) => fieldBounds(f))),
    targets,
  };
}

function snap(g, dx, dy, noSnap) {
  const guides = [];
  if (noSnap) return { dx, dy, guides };
  const th = SNAP_PX / ppu();
  const b = { ...g.bounds0, x: g.bounds0.x + dx, y: g.bounds0.y + dy };
  const W = S.label.width;
  const H = S.label.height;
  const xs = [b.x, b.x + b.w / 2, b.x + b.w];
  const ys = [b.y, b.y + b.h / 2, b.y + b.h];
  const tx = [0, W / 2, W];
  const ty = [0, H / 2, H];
  for (const t of g.targets) {
    tx.push(t.x, t.x + t.w / 2, t.x + t.w);
    ty.push(t.y, t.y + t.h / 2, t.y + t.h);
  }
  let bestX = null;
  let bestY = null;
  for (const a of tx) for (const m of xs) if (Math.abs(a - m) < th && (bestX === null || Math.abs(a - m) < Math.abs(bestX.d))) bestX = { d: a - m, at: a };
  for (const a of ty) for (const m of ys) if (Math.abs(a - m) < th && (bestY === null || Math.abs(a - m) < Math.abs(bestY.d))) bestY = { d: a - m, at: a };
  if (bestX) {
    dx += bestX.d;
    guides.push({ x1: bestX.at, y1: 0, x2: bestX.at, y2: H });
  }
  if (bestY) {
    dy += bestY.d;
    guides.push({ x1: 0, y1: bestY.at, x2: W, y2: bestY.at });
  }
  return { dx, dy, guides };
}

function moveDrag(g, e, pt) {
  let dx = pt.x - g.start.x;
  let dy = pt.y - g.start.y;
  if (!g.moved) {
    if (Math.hypot(dx, dy) * ppu() < DRAG_START_PX) return;
    g.moved = true;
    S.dragging = true;
  }
  if (e.shiftKey) {
    if (!g.axis) g.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
    if (g.axis === 'x') dy = 0;
    else dx = 0;
  } else g.axis = null;
  const snapped = snap(g, dx, dy, e.altKey || e.metaKey || e.ctrlKey);
  // keep the group on the label's positive side
  const minDx = -Math.min(...g.initial.map((i) => i.x));
  const minDy = -Math.min(...g.initial.map((i) => i.y));
  dx = Math.round(Math.max(minDx, snapped.dx));
  dy = Math.round(Math.max(minDy, snapped.dy));
  S.guides = snapped.guides;
  try {
    mutate(
      (label) => {
        for (const i of g.initial) updateField(label, i.key, { x: i.x + dx, y: i.y + dy });
      },
      { live: true },
    );
  } catch {
    /* rejected position (e.g. out of range): keep the last valid one */
  }
  emit('overlay');
}

// ----- resize ------------------------------------------------------------------------------------
function startResize(pt, dir) {
  const f = S.byKey.get(S.selection[0]);
  if (!f || isLocked(f)) return null;
  return { type: 'resize', start: pt, dir, f0: structuredClone(f), box0: localBox(f), moved: false };
}

function moveResize(g, e, pt) {
  const { f0, box0, dir } = g;
  const d = rotateVec(pt.x - g.start.x, pt.y - g.start.y, -(f0.rotation || 0));
  if (!g.moved) {
    if (Math.hypot(d.x, d.y) * ppu() < DRAG_START_PX) return;
    g.moved = true;
    S.dragging = true;
  }
  const hasE = dir.includes('e');
  const hasW = dir.includes('w');
  const hasN = dir.includes('n');
  const hasS = dir.includes('s');
  const dW = hasE ? d.x : hasW ? -d.x : 0;
  const dH = hasS ? d.y : hasN ? -d.y : 0;
  const corner = (hasE || hasW) && (hasN || hasS);
  const diag = corner ? 1 + (dW * box0.w + dH * box0.h) / (box0.w * box0.w + box0.h * box0.h) : 1;
  let patch = null;

  if (f0.kind === 'text' || f0.kind === 'date') {
    const font = f0.font;
    let height = font.height;
    let width = font.width;
    if (corner) {
      const sc = Math.max(40 / font.height, diag);
      height = font.height * sc;
      width = font.width * sc;
    } else if (hasE || hasW) {
      width = font.width * Math.max(0.05, (box0.w + dW) / box0.w);
    } else {
      height = font.height * Math.max(0.05, (box0.h + dH) / box0.h);
    }
    patch = { font: { height: Math.max(40, Math.round(height)), width: Math.max(10, Math.round(width)) } };
  } else if (f0.kind === 'barcode') {
    const dots = (units) => Math.max(1, Math.round(units / DOT));
    const toUnits = (n) => Math.round(n * DOT);
    if (isOneD(f0)) {
      patch = {};
      if (hasN || hasS) patch.barHeight = Math.max(100, Math.round((f0.barHeight || 1000) + (corner ? (diag - 1) * box0.h : dH)));
      if (hasE || hasW) {
        const sc = corner ? diag : (box0.w + dW) / box0.w;
        patch.narrowBarWidth = toUnits(dots((f0.narrowBarWidth || DOT * 3) * Math.max(0.05, sc)));
      }
    } else {
      const sc = corner ? diag : hasE || hasW ? (box0.w + dW) / box0.w : (box0.h + dH) / box0.h;
      patch = { cellWidth: toUnits(dots((f0.cellWidth || DOT * 4) * Math.max(0.05, sc))) };
    }
  }
  if (!patch) return;

  // Keep the opposite edge/corner fixed.
  const merged = { ...f0, ...patch, font: patch.font ? { ...f0.font, ...patch.font } : f0.font };
  const box1 = localBox(merged);
  const ax = (b) => (hasW ? b.x + b.w : b.x);
  const ay = (b) => (hasN ? b.y + b.h : b.y);
  const a0 = rotateVec(ax(box0), ay(box0), f0.rotation || 0);
  const a1 = rotateVec(ax(box1), ay(box1), f0.rotation || 0);
  patch.x = Math.max(0, Math.round(f0.x + a0.x - a1.x));
  patch.y = Math.max(0, Math.round(f0.y + a0.y - a1.y));
  try {
    mutate((label) => updateField(label, f0.key, patch), { live: true });
  } catch {
    /* rejected size: keep the last valid one */
  }
  emit('overlay');
}

// ----- marquee ----------------------------------------------------------------------------------
function moveMarquee(g, pt) {
  const x = Math.min(g.start.x, pt.x);
  const y = Math.min(g.start.y, pt.y);
  const w = Math.abs(pt.x - g.start.x);
  const hgt = Math.abs(pt.y - g.start.y);
  if (!g.moved && Math.hypot(w, hgt) * ppu() < DRAG_START_PX) return;
  g.moved = true;
  S.marquee = { x, y, w, h: hgt };
  const hits = S.fields.filter((f) => intersects(fieldBounds(f), S.marquee)).map((f) => f.key);
  S.selection = g.additive ? [...new Set([...g.base, ...hits])] : hits;
  emit('selection', 'overlay');
}

// ----- pointer wiring ------------------------------------------------------------------------------
function onPointerDown(e) {
  if (!S.label || e.button !== 0) return;
  const svg = $('#label-svg');
  const pt = svgPoint(e);
  const handle = e.target.closest && e.target.closest('[data-handle]');
  const rotate = e.target.closest && e.target.closest('[data-rotate]');
  if (rotate) {
    e.preventDefault();
    gesture = { type: 'rotate' };
  } else if (handle) {
    e.preventDefault();
    gesture = startResize(pt, handle.dataset.handle);
  } else {
    const hit = hitTest(pt);
    if (hit) {
      e.preventDefault();
      gesture = startDrag(e, pt, hit);
    } else {
      gesture = { type: 'marquee', start: pt, moved: false, additive: e.shiftKey || e.metaKey || e.ctrlKey, base: [...S.selection] };
    }
  }
  if (gesture) {
    try {
      svg.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }
  $('#canvas-scroll').focus({ preventScroll: true });
}

function onPointerMove(e) {
  if (!S.label) return;
  const pt = svgPoint(e);
  updateCursor(e, pt);
  if (!gesture) {
    const hit = hitTest(pt);
    const key = hit ? hit.key : null;
    if (key !== S.hovered) {
      S.hovered = key;
      emit('hover');
    }
    $('#label-svg').style.cursor = hit && !isLocked(hit) ? 'move' : 'default';
    return;
  }
  if (gesture.type === 'drag') moveDrag(gesture, e, pt);
  else if (gesture.type === 'resize') moveResize(gesture, e, pt);
  else if (gesture.type === 'marquee') moveMarquee(gesture, pt);
}

function onPointerUp(e) {
  const g = gesture;
  gesture = null;
  if (!g) return;
  const wasDragging = S.dragging;
  S.dragging = false;
  S.guides = null;
  if (g.type === 'rotate') A.rotateSelected(90);
  else if (g.type === 'drag') {
    if (g.moved) commit(`drag:${Date.now()}`);
    else if (S.selection.length > 1 && !(e.shiftKey || e.metaKey || e.ctrlKey)) select([g.clickKey]);
  } else if (g.type === 'resize') {
    if (g.moved) commit(`resize:${Date.now()}`);
  } else if (g.type === 'marquee') {
    S.marquee = null;
    if (!g.moved && !g.additive) select([]);
  }
  if (wasDragging || g.type === 'marquee') emit('overlay', 'selection');
}

function updateCursor(e, pt) {
  const area = $('#canvas-area');
  const inside = pt.x >= 0 && pt.y >= 0 && pt.x <= S.label.width && pt.y <= S.label.height;
  area.classList.toggle('tracking', inside);
  $('#stat-cursor').textContent = inside ? `${(pt.x / 100).toFixed(2)}, ${(pt.y / 100).toFixed(2)} mm` : '—';
  if (!inside) return;
  $('#ruler-cursor-h').style.left = `${e.clientX - $('#ruler-h').getBoundingClientRect().left}px`;
  $('#ruler-cursor-v').style.top = `${e.clientY - $('#ruler-v').getBoundingClientRect().top}px`;
}

export function initInteraction() {
  const svg = $('#label-svg');
  const scroll = $('#canvas-scroll');
  svg.addEventListener('pointerdown', onPointerDown);
  scroll.addEventListener('pointerdown', (e) => {
    // clicks on the grey stage (outside the label) clear the selection
    if (S.label && e.button === 0 && !svg.contains(e.target) && !e.target.closest('.welcome-card')) select([]);
  });
  svg.addEventListener('pointermove', onPointerMove);
  svg.addEventListener('pointerup', onPointerUp);
  svg.addEventListener('pointercancel', onPointerUp);
  svg.addEventListener('lostpointercapture', (e) => {
    if (gesture) onPointerUp(e);
  });
  svg.addEventListener('pointerleave', () => {
    if (gesture) return;
    $('#canvas-area').classList.remove('tracking');
    $('#stat-cursor').textContent = '—';
    if (S.hovered) {
      S.hovered = null;
      emit('hover');
    }
  });
  svg.addEventListener('dblclick', (e) => {
    const hit = S.label && hitTest(svgPoint(e));
    if (hit && hit.kind !== 'unsupported') {
      select([hit.key]);
      document.dispatchEvent(new CustomEvent('labels:focus-content', { detail: { key: hit.key } }));
    }
  });
  scroll.addEventListener('contextmenu', (e) => {
    if (!S.label) return;
    const pt = svgPoint(e);
    const onLabel = svg.contains(e.target);
    e.preventDefault();
    const hit = onLabel ? hitTest(pt) : null;
    if (hit) openFieldContextMenu(e.clientX, e.clientY, hit.key);
    else openCanvasContextMenu(e.clientX, e.clientY, onLabel ? pt : null);
  });
  scroll.addEventListener(
    'wheel',
    (e) => {
      if (!S.label || !(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const pt = svgPoint(e);
      setZoom(S.zoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12), { ux: pt.x, uy: pt.y, clientX: e.clientX, clientY: e.clientY });
    },
    { passive: false },
  );
  scroll.addEventListener('keydown', (e) => {
    if ((e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) && S.selection.length) {
      e.preventDefault();
      const r = scroll.getBoundingClientRect();
      openFieldContextMenu(r.left + r.width / 2, r.top + r.height / 2, S.selection[0]);
    }
  });
}

export { field };
